// landing/sections/StatsStrip.tsx — three honest numbers under the hero copy.
//
// Numbers come from /api/stats/ (real counts, cached server-side — see
// backend apps/content/stats.py). Every state reserves at least 56px and wraps
// on narrow screens: em-dashes while loading, the numbers on success, and
// on failure a link to the Council's public record instead —
// words, never made-up figures. Display rounds (12,480 → "12.4k"); rounding a
// real count is coarse, inventing one would be lying.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { statsService } from '@/core/services';
import type { SiteStats } from '@/core/services';

const STRIP_HEIGHT = 56;

const Strip = styled.dl`
  display: flex;
  align-items: center;
  gap: 20px;
  min-height: ${STRIP_HEIGHT}px;
  margin: 0;
  padding-top: 8px;
  flex-wrap: wrap;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-right: 20px;
  border-right: 1px solid ${({ theme }) => theme.color.glassBorder};
  &:last-child {
    border-right: 0;
    padding-right: 0;
  }
`;

const Value = styled.dd`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
  font-variant-numeric: tabular-nums;
`;

const Label = styled.dt`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* The zero case. A "0" in the same weight as the other two figures reads as a
   score, and the reader is left to guess whether it means nothing has happened
   or nothing is being shown. It is the first: a report becomes a case only once
   enough independent reports agree, so on a platform this new the number is
   honestly zero. Say that, in words, in the same 56px the digits occupied — and
   link the record, which is now readable without an account. */
const NoneYet = styled(Link)`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.35;
  max-width: 34ch;
  color: ${({ theme }) => theme.color.textMuted};
  text-decoration: none;
  b {
    color: ${({ theme }) => theme.color.textPrimary};
    font-weight: 600;
  }
  &:hover b {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Fallback = styled.p`
  display: flex;
  align-items: center;
  min-height: ${STRIP_HEIGHT}px;
  flex-wrap: wrap;
  margin: 0;
  padding-top: 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};

  a {
    color: ${({ theme }) => theme.color.accentCyan};
    text-decoration: none;
    margin-left: 6px;
    &:hover {
      text-decoration: underline;
    }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.color.focusRing};
      outline-offset: 2px;
    }
  }
`;

/** 12480 → "12.4k" (coarse but real; small counts stay exact). */
export function compactCount(n: number): string {
  if (n < 1000) return String(n);
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

export function StatsStrip() {
  const [stats, setStats] = useState<SiteStats | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    statsService
      .fetchSiteStats()
      .then((s) => {
        if (!cancelled) setStats(s);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (failed) {
    return (
      <Fallback data-testid="stats-fallback">
        An independent platform with a public moderation record:
        <Link to="/council">see the Council</Link>
      </Fallback>
    );
  }

  const cells: { label: string; value: number | null }[] = [
    { label: 'Members', value: stats?.members ?? null },
    { label: 'Posts', value: stats?.posts ?? null },
  ];

  return (
    <Strip aria-label="TruXSocial in numbers" data-testid="stats-strip">
      {cells.map((c) => (
        <Stat key={c.label}>
          <Value>{c.value === null ? 'Loading' : compactCount(c.value)}</Value>
          <Label>{c.label}</Label>
        </Stat>
      ))}
      {stats !== null && stats.verdicts === 0 ? (
        <NoneYet to="/council" data-testid="stats-no-verdicts">
          <b>No verdicts yet</b>: no report has met the threshold to open a case. The record is
          public either way.
        </NoneYet>
      ) : (
        <Stat>
          <Value>{stats === null ? 'Loading' : compactCount(stats.verdicts)}</Value>
          <Label>Public verdicts</Label>
        </Stat>
      )}
    </Strip>
  );
}
